<template>
  <MetaInfo :title="meta.title" :description="meta.description"></MetaInfo>

  <navigation
    announcementText="Let AI bring your email content to life."
    :announcementLink="{
      url: '/features/ai-content-assistant',
      text: 'Try our new AI Content Assistant',
    }"
    announcementIcon="ai-outline-dark"
  ></navigation>

  <PageHeader
    title="The marketing platform for agencies"
    subtitle-text="One client is easy. Ten clients across ten tools is chaos. Transpond puts every client's campaigns, automations, and reporting in one place, with native CRM sync and white label built in."
  >
    <PageHeaderRating
      button-text="Get started"
      rating-text=""
    ></PageHeaderRating>
  </PageHeader>

  <div class="homepage-hero__image-container hero-fade-in hero-fade-in--delay-3">
    <img
      src="../assets/images/home/hero-weiss-moller.webp"
      alt="Agency dashboard showing multiple client accounts"
      class="email-editor-screenshot"
    />
  </div>

  <!-- Why Transpond Section -->
  <div class="why-transpond-section">
    <div class="why-transpond-container">
      <h3 class="mb-8">Why Transpond is perfect for agencies</h3>

      <div class="why-transpond-grid">
        <router-link to="/features/campaignbuilder/" class="why-transpond-card">
          <img
            src="../assets/why-transpond_brand.png"
            alt="Campaigns and Template Builder"
            class="why-transpond-card__image"
          />
          <h5 class="mb-2 mt-5">Campaign Builder</h5>
          <p class="why-transpond-description">
            Create responsive email templates with the Transpond drag-and-drop 
            editor. Save them by client, keep every account on brand, and 
            build from scratch or adapt a pre-built template in minutes. 
            Then reuse them across clients.
          </p>
        </router-link>

        <div class="why-transpond-card">
          <router-link to="/features/automations/" class="why-transpond-card__main">
            <img
              src="../assets/why-agencies-automations.png"
              alt="Automations"
              class="why-transpond-card__image"
            />
            <h5 class="mb-2 mt-5">Automations</h5>
            <p class="why-transpond-description">
              Build an automation once, then adapt it for the next client. Welcome
              sequences, re-engagement, and follow-ups run without a spreadsheet or a Zap.
            </p>
          </router-link>
          <p class="why-transpond-card__footer">
            Read about
            <router-link
              to="/blog/marketing-automation-for-agencies-what-it-is-why-its-useful-and-how-to-get-started/"
              class="text-link text-link--bare"
            >agency automation</router-link>
            and
            <router-link
              to="/blog/follow-up-automation-for-agencies-stop-client-leads-going-cold/"
              class="text-link text-link--bare"
            >client follow-ups</router-link>.
          </p>
        </div>

        <router-link
          to="/blog/how-to-white-label-email-marketing-with-transpond/"
          class="why-transpond-card"
        >
          <img
            src="../assets/why-transpond_white_label.png"
            alt="White label built in"
            class="why-transpond-card__image"
          />
          <h5 class="mb-2 mt-5">White label built in</h5>
          <p class="why-transpond-description">
            Put your agency's name on the work. Present campaigns, reports, and
            dashboards under your own brand, so clients see your agency, not the
            software behind it. Login, reporting, and sending stay on brand.
          </p>
        </router-link>
      </div>
    </div>
  </div>

  <!-- How Transpond Works Section -->
  <div class="how-it-works-container">
    <div class="divider--dashed"></div>

    <div class="step-section">
      <h4 class="step-headline">Run every client campaign from one place</h4>

      <div class="bento-grid">
        <router-link to="/features/analytics/" class="bento-card bento-card--half">
          <h5>Analytics your clients will actually read</h5>
          <p>
            See opens, clicks, and engagement in real time, then turn them into
            client-ready reporting that helps you prove the value of each campaign.
          </p>
          <img
            src="../assets/homepage-analytics.png"
            alt="Analytics"
            class="bento-card__image"
          />
        </router-link>

        <router-link to="/features/signupforms/" class="bento-card bento-card--half">
          <h5>Forms</h5>
          <p>
            Build drag-and-drop forms for client emails, websites, and social media.
            Every submission lands in the right client account.
          </p>
          <img
            src="../assets/homepage-signup_forms.png"
            alt="Signup forms"
            class="bento-card__image"
          />
        </router-link>

        <router-link
          to="/blog/how-to-replace-your-entire-agency-tool-stack-with-transpond/"
          class="bento-card bento-card--half"
        >
          <h5>Replace your agency tool stack</h5>
          <p>
            Most agencies run a patchwork: one tool for email, another for automation,
            a third for forms, and Zapier holding it all together. Transpond replaces
            the stack with one platform.
          </p>
          <img
            src="../assets/homepage-brand.png"
            alt="Replace your agency tool stack"
            class="bento-card__image"
          />
        </router-link>

        <router-link to="/blog/crm-marketing-campaigns-how-to-maximize-existing-data-without-manual-exports/" class="bento-card bento-card--half">
          <h5>Native CRM sync</h5>
          <p>
            Your clients live in their CRMs, so Transpond syncs with them natively.
            Contacts, tags, and segments flow through automatically: no CSV exports,
            no fragile Zapier workflows.
          </p>
          <img
            src="../assets/homepage-crm.png"
            alt="Native CRM sync"
            class="bento-card__image"
          />
        </router-link>
      </div>
    </div>

    <div class="step-section mt-12 sm:mt-24">
      <h4 class="step-headline">And everything else an agency needs</h4>

      <div class="agency-feature-grid">
        <router-link to="/features/websitetracking/" class="agency-feature-card">
          <h5 class="agency-feature-card__title">
            <img
              src="../assets/images/icons/website-tracking-filled-light.svg"
              alt=""
              class="agency-feature-card__icon"
            />
            Website Tracking
          </h5>
          <p>
            See what client subscribers do after the click. Track page visits
            after email links and set up follow-ups when it matters.
          </p>
        </router-link>

        <a
          href="https://transpond.io/support/deliverability-and-compliance/list-cleaning"
          class="agency-feature-card"
          target="_blank"
          rel="noopener noreferrer"
        >
          <h5 class="agency-feature-card__title">
            <img
              src="../assets/images/icons/list-cleaning-filled-light.svg"
              alt=""
              class="agency-feature-card__icon"
            />
            List Cleaning
          </h5>
          <p>
            Keep every client's deliverability healthy without manual audits.
            Invalid and unused addresses are filtered out automatically.
          </p>
        </a>

        <a
          href="https://transpond.io/support/creating-and-sending-campaigns/sending-an-email-campaign"
          class="agency-feature-card"
          target="_blank"
          rel="noopener noreferrer"
        >
          <h5 class="agency-feature-card__title">
            <img
              src="../assets/images/icons/social-campaigns-filled-light.svg"
              alt=""
              class="agency-feature-card__icon"
            />
            Campaign Wizard
          </h5>
          <p>
            Get new client accounts sending faster. Send to multiple groups,
            add segments, and check inbox placement before you hit send.
          </p>
        </a>

        <router-link to="/features/transactionalemails/" class="agency-feature-card">
          <h5 class="agency-feature-card__title">
            <img
              src="../assets/images/icons/transactional-filled-light.svg"
              alt=""
              class="agency-feature-card__icon"
            />
            Transactional Email
          </h5>
          <p>
            Send order confirmations and onboarding emails direct
            from a client's website or app, with full analytics.
          </p>
        </router-link>
      </div>
    </div>
  </div>

  <div class="divider--dashed"></div>

  <section class="agency-banner">
    <div class="agency-banner__container">
      <div class="agency-banner__content">
        <div class="agency-banner__text">
          <span class="heading-kicker">Free download</span>
          <h2 class="agency-banner__title">
            Agency campaign setup checklist
          </h2>
          <p class="agency-banner__subtitle">
            Onboarding a new client? Grab our free checklist covering everything from
            CRM sync to first send.
          </p>
        </div>
        <a
          href="/assets/agency-campaign-setup-checklist.pdf"
          class="btn btn--primary"
          download="Agency Campaign Setup Checklist.pdf"
        >
          Download the checklist
        </a>
      </div>
    </div>
  </section>

  <section class="content-section mt-20 sm:mt-40 mb-20 sm:mb-40">
    <div class="container-fluid">
      <div class="row between-xs middle-xs">
        <div class="col-xs-12 col-sm-6 mb-10 sm:mb-0">
          <img
            src="../assets/campaigns_asset-small.png"
            alt="Native CRM sync and integrations"
            class="content-section__image"
            width="610"
            height="456"
          />
        </div>
        <div class="col-xs-12 col-sm-5">
          <h4>Still comparing your options?</h4>
          <p class="p--lg">
            See how Transpond stacks up for agency work:
          </p>
          <ul class="agency-compare__list mt-8">
            <li>
              <h5>
                <router-link
                  to="/blog/why-transpond-is-the-mailchimp-alternative-youve-been-looking-for/"
                  class="text-link"
                >
                  Transpond vs Mailchimp
                </router-link>
              </h5>
            </li>
            <li>
              <h5>
                <router-link
                  to="/blog/5-best-mailchimp-alternatives-for-small-businesses-in-2026/"
                  class="text-link"
                >
                  The best Mailchimp alternative for agencies
                </router-link>
              </h5>
            </li>
            <li>
              <h5>
                <router-link
                  to="/blog/transpond-vs-activecampaign-which-marketing-platform-is-best-for-you/"
                  class="text-link"
                >
                  Transpond vs ActiveCampaign
                </router-link>
              </h5>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <section class="footer-cta">
    <div class="footer-cta__container">
      <hr />
      <div class="footer-cta__content">
        <h2 class="h1">One platform. Every client.</h2>
        <p class="footer-cta__subtitle">
          Stop patching tools together and run your whole client roster from one place.
          Try Transpond free today.
        </p>
        <router-link
          :to="{ name: 'Pricing' }"
          title="Get started"
          class="btn btn--primary mt-10"
        >
          Get started
        </router-link>
      </div>
    </div>
  </section>

  <site-footer></site-footer>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import PageHeader from "@/components/Header/PageHeader";
import PageHeaderRating from "@/components/Header/PageHeaderRating";
import SiteFooter from "@/components/SiteFooter";

export default {
  name: "MarketingForAgencies",
  components: {
    MetaInfo,
    Navigation,
    PageHeader,
    PageHeaderRating,
    SiteFooter,
  },
  data() {
    return {
      meta: {
        title: "Marketing Platform for Agencies | Transpond",
        description:
          "Transpond puts every client's campaigns, automations, and reporting in one place, with native CRM sync and white label built in.",
      },
    };
  },
};
</script>
